import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Apple, Plus, Pencil, Trash2 } from "lucide-react";

export function AddFoodDialog({ open, onOpenChange, targetMeal, foodFilter, setFoodFilter, catFilter, setCatFilter, filteredFoods, foodCategories, addFoodToMeal, showCreateFood, setShowCreateFood, newFood, setNewFood, handleCreateFood, editingFood, setEditingFood, handleDeleteFood, getFoodName, getCatName, L, mealConfigDE }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Apple className="w-5 h-5 text-emerald-500" /> Lebensmittel-Datenbank</DialogTitle>
          <DialogDescription>Lebensmittel zum {mealConfigDE} hinzufügen</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-2">
          <Input placeholder={L.search} value={foodFilter} onChange={e => setFoodFilter(e.target.value)} className="flex-1" data-testid="food-search" />
          <Button size="sm" variant={showCreateFood ? "secondary" : "default"} onClick={() => setShowCreateFood(!showCreateFood)} data-testid="toggle-create-food">
            <Plus className="w-4 h-4 mr-1" /> {L.newFoodBtn}
          </Button>
        </div>

        {showCreateFood && (
          <div className="border border-emerald-200 rounded-lg p-3 mb-2 bg-emerald-50/50 space-y-2" data-testid="create-food-form">
            <Input placeholder="Name *" value={newFood.name} onChange={e => setNewFood(p => ({ ...p, name: e.target.value }))} data-testid="new-food-name" />
            <div className="flex flex-wrap gap-1.5">
              {foodCategories.map(c => (
                <button key={c.id} onClick={() => setNewFood(p => ({ ...p, category: c.id }))}
                  className={`px-2 py-1 rounded text-xs font-medium ${newFood.category === c.id ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}>
                  {getCatName(c)}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-5 gap-2">
              <div>
                <label className="text-[10px] font-semibold text-slate-500">{L.portion}</label>
                <Input type="number" value={newFood.serving_size} onChange={e => setNewFood(p => ({ ...p, serving_size: e.target.value }))} data-testid="new-food-serving" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500">{L.calories} /{newFood.serving_size || 100}g</label>
                <Input type="number" value={newFood.calories} onChange={e => setNewFood(p => ({ ...p, calories: e.target.value }))} data-testid="new-food-calories" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-blue-600">{L.proteinG}</label>
                <Input type="number" value={newFood.protein} onChange={e => setNewFood(p => ({ ...p, protein: e.target.value }))} data-testid="new-food-protein" className="border-blue-200" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-amber-600">{L.carbsG}</label>
                <Input type="number" value={newFood.carbs} onChange={e => setNewFood(p => ({ ...p, carbs: e.target.value }))} data-testid="new-food-carbs" className="border-amber-200" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-rose-600">{L.fatG}</label>
                <Input type="number" value={newFood.fat} onChange={e => setNewFood(p => ({ ...p, fat: e.target.value }))} data-testid="new-food-fat" className="border-rose-200" />
              </div>
            </div>
            <textarea placeholder={L.description}
              value={newFood.description || ""} onChange={e => setNewFood(p => ({ ...p, description: e.target.value }))}
              className="w-full text-xs p-2 border border-slate-200 rounded-md resize-y min-h-[40px] focus:outline-none focus:border-emerald-400" rows={2} data-testid="new-food-desc" />
            <Button size="sm" onClick={handleCreateFood} className="w-full" data-testid="submit-new-food">{L.createFood}</Button>
          </div>
        )}

        <div className="flex flex-wrap gap-1.5 mb-2">
          <button onClick={() => setCatFilter("")} className={`px-2 py-0.5 rounded text-[11px] font-medium ${!catFilter ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{L.allCats}</button>
          {foodCategories.map(c => (
            <button key={c.id} onClick={() => setCatFilter(c.id)}
              className={`px-2 py-0.5 rounded text-[11px] font-medium ${catFilter === c.id ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
              {getCatName(c)}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-auto space-y-1.5">
          {filteredFoods.map(food => (
            <div key={food.id} className="flex items-center gap-3 p-2 rounded-lg border border-slate-100 hover:bg-emerald-50 group">
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => addFoodToMeal(food)}>
                <div className="flex items-center gap-1">
                  <p className="text-sm font-semibold text-slate-900">{getFoodName(food)}</p>
                  {food.custom && <span className="text-[9px] px-1 py-0.5 bg-emerald-100 text-emerald-600 rounded font-medium">Custom</span>}
                </div>
                <p className="text-[10px] text-slate-400">{food.calories} kcal • {L.pAbbr}:{food.protein}g • {L.kAbbr}:{food.carbs}g • {L.fAbbr}:{food.fat}g /{food.serving_size || 100}g</p>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={(e) => { e.stopPropagation(); setEditingFood({ ...food }); }} className="p-1 rounded hover:bg-blue-50 text-blue-400 opacity-0 group-hover:opacity-100" data-testid={`edit-food-${food.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={(e) => handleDeleteFood(e, food)} className="p-1 rounded hover:bg-red-50 text-red-400 opacity-0 group-hover:opacity-100" data-testid={`delete-food-${food.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                <Plus className="w-4 h-4 text-emerald-500 cursor-pointer" onClick={() => addFoodToMeal(food)} />
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
